import { PauseCircle } from 'lucide-react';
import { useAssistantRuntime, useAuiState } from '@assistant-ui/react';
import { Button } from '@/components/ui';

/**
 * Inline notice for a run that stopped `incomplete` — the agent ran out of its
 * agentic-loop iteration budget and never produced a final answer, so the text
 * above this notice is a progress summary rather than an answer.
 *
 * This is a PAUSE, not a failure. The runtime already wrote a transcript
 * checkpoint, so the next turn in this conversation continues from where the
 * agent stopped instead of restarting — which is all "Continue" has to do: send
 * an ordinary message. Deliberately neutral chrome (never the red error alert):
 * an iteration cap says nothing about whether the work succeeded, and framing it
 * as an error made users read a successful build as a broken one.
 */
export function RunPausedNotice() {
  const messageMetadata = useAuiState((s) => s.message.metadata);
  const runtime = useAssistantRuntime();
  const custom = messageMetadata?.custom as Record<string, unknown> | undefined;

  if (!custom?.runIncomplete) return null;

  const resumable = custom.resumable === true;
  const stopReason = custom.stopReason as string | undefined;

  const reasonText =
    stopReason === 'NO_PROGRESS'
      ? 'The agent stopped making progress and paused before finishing.'
      : 'The agent ran out of working steps and paused before finishing.';

  return (
    <div className="my-2 flex gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-300">
      <PauseCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">Paused before finishing</p>
        <p className="mt-1 text-amber-300/90">
          {reasonText} Everything above is done and saved
          {resumable ? ' — continue and it picks up where it stopped' : ''}.
        </p>
        {resumable && (
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() =>
              runtime.thread.append({
                role: 'user',
                content: [{ type: 'text', text: 'Continue.' }],
              })
            }
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
}
